import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + 24 * 60 * 60 * 1000;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + 24 * 60 * 60 * 1000;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(24 * 60 * 60 * 1000);
  useEffect(() => {
    const t = getInitialTarget();
    setTarget(t);
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        // restart another 24h cycle for urgency
        const next = Date.now() + 24 * 60 * 60 * 1000;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(24 * 60 * 60 * 1000);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  // Simulated progress: starts around 65% and slowly increases toward 90% as time runs out
  const elapsed = 24 * 60 * 60 * 1000 - remaining;
  const progress = Math.min(90, 65 + elapsed / (24 * 60 * 60 * 1000) * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-green-dark border border-gold/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(20,83,45,0.15)]" data-id="element-41">
      <div className="text-center" data-id="element-42">
        <div className="flex items-center justify-center gap-2 text-gold font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-43">
          <AlarmClock size={20} className="animate-pulse" data-id="element-44" />
          <span data-id="element-45">El precio de lanzamiento termina en:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-46">
          <TimeBox value={pad(totalHours)} label="hs" data-id="element-47" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-48">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-49" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-50">:</span>
          <TimeBox value={pad(seconds)} label="seg" data-id="element-51" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-cream mb-8" data-id="element-52">
          Después sube el precio y no vuelve a bajar.
        </p>

        <div className="space-y-2" data-id="element-53">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-54">
            <span className="text-gold-light" data-id="element-55">
              Cupos al precio de lanzamiento
            </span>
            <span className="text-gold" data-id="element-56">Quedan pocos</span>
          </div>
          <div className="w-full h-2.5 bg-black/30 rounded-full overflow-hidden border border-gold/30" data-id="element-57">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gold-gradient rounded-full shadow-[0_0_10px_rgba(212,175,55,0.6)]" data-id="element-58" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-gold-light font-medium" data-id="element-59">
            {Math.round(progress)}% de los cupos tomados
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-black/20 border-2 border-gold/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-[inset_0_0_15px_rgba(212,175,55,0.1)]" data-id="element-60">
      <div className="flex items-baseline justify-center gap-1" data-id="element-61">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-62">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-gold-light" data-id="element-63">
          {label}
        </span>
      </div>
    </div>;
}